/**
 * Visible Block Tracker
 * Tracks which type of block is currently visible in the viewport
 */

import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { ContentBlockType } from '../../services/contentService';

interface VisibleBlockTrackerContextType {
  visibleBlockType: ContentBlockType | null;
  setVisibleBlockType: (type: ContentBlockType | null) => void;
}

export const VisibleBlockTrackerContext = createContext<VisibleBlockTrackerContextType | undefined>(undefined);

export const VisibleBlockTrackerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [visibleBlockType, setVisibleBlockType] = useState<ContentBlockType | null>(null);

  return (
    <VisibleBlockTrackerContext.Provider value={{ visibleBlockType, setVisibleBlockType }}>
      {children}
    </VisibleBlockTrackerContext.Provider>
  );
};

export const useVisibleBlockTracker = () => {
  const context = useContext(VisibleBlockTrackerContext);
  if (context === undefined) {
    throw new Error('useVisibleBlockTracker must be used within a VisibleBlockTrackerProvider');
  }
  return context;
};

/** Resets visible block type when not on lesson view so the font-size slider is disabled on dashboard etc. */
export const VisibleBlockTrackerResetter: React.FC<{ currentView: string }> = ({ currentView }) => {
  const { setVisibleBlockType } = useVisibleBlockTracker();
  useEffect(() => {
    if (currentView !== 'lesson') {
      setVisibleBlockType(null);
    }
  }, [currentView, setVisibleBlockType]);
  return null;
};

/**
 * Hook to track when a block becomes visible
 */
export const useBlockVisibility = (blockType: ContentBlockType, blockRef: React.RefObject<HTMLDivElement>) => {
  const { setVisibleBlockType } = useVisibleBlockTracker();

  useEffect(() => {
    if (!blockRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setVisibleBlockType(blockType);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(blockRef.current);

    return () => {
      observer.disconnect();
    };
  }, [blockType, blockRef, setVisibleBlockType]);
};

